import { isValidIban, parseMoneyToCents, tt } from '@agos/shared'

/**
 * The platform's own billing identity — who it invoices as, and how it expects to be paid.
 *
 * **FB-S06 — PayBySquare is on and the IBAN is empty.** `InvoiceDocument.drawSquare` skips the QR when
 * the IBAN is blank, which is right: a QR built on a blank account is a scannable instruction to pay
 * nobody. But nothing says the skip happened, and the IBAN line falls back to
 * `formatIban(orDash(...))` — so every invoice prints `IBAN —` under a bank name. **3 434,16 €** has
 * been invoiced that way across four months.
 *
 * So a switch's **state** and its **effect** are two facts here, and only the first was on screen. This
 * module answers both.
 *
 * The IBAN is checked with `isValidIban` — the same mod-97 every other IBAN in this repository goes
 * through — so a wrong account is caught here rather than by somebody's bank.
 */

export interface PlatformBillingSettings {
  companyName: string
  ico: string
  dic: string
  icDph: string
  iban: string
  swift: string
  bankName: string
  fromEmail: string
  invoiceNumberPrefix: string
  /**
   * What the next invoice will really be called - §3.29, answered by the server.
   *
   * `InvoiceNumbers.next` generates it, and the settings read publishes the same call the raise path
   * makes. Empty when the payload predates the field, which is the only case
   * {@link numberingExample} still derives anything for.
   */
  nextInvoiceNumber: string
  defaultDueDays: number
  defaultVatRatePct: number
  revenueSharePct: number
  autoBillingEnabled: boolean
  autoEmailInvoices: boolean
  paymentSquareEnabled: boolean
  // ─── §3.14 - the rest of what `PlatformBillingSettingsService` accepts ────────────────────────
  //
  // Read because the form that sets them is here now, and a form offering nine of twenty-one fields
  // would leave the other twelve settable only by SQL - which is the finding this section is about,
  // one level down.
  address: string
  city: string
  zip: string
  country: string
  registrationNote: string
  fromName: string
  footerText: string
  logoUrl: string
  accentColor: string
  invoiceEmailSubject: string
  invoiceEmailBody: string
  /**
   * Whether a Resend key is stored - **a boolean, never the key**.
   *
   * The service answers `resendApiKeySet` rather than a masked string, deliberately: its own note
   * says *"masking is not deletion"*, and a boolean cannot be round-tripped back as the key. So the
   * form has nothing to pre-fill and sends the key only when somebody types one.
   */
  resendApiKeySet: boolean
}

function text(row: Record<string, unknown>, key: string): string {
  const value = row[key]
  return typeof value === 'string' ? value.trim() : ''
}

function num(row: Record<string, unknown>, key: string): number {
  const value = row[key]
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function platformBillingOf(payload: unknown): PlatformBillingSettings {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const row = (typeof outer.settings === 'object' && outer.settings !== null ? outer.settings : outer) as Record<string, unknown>
  return {
    companyName: text(row, 'companyName'),
    ico: text(row, 'ico'),
    dic: text(row, 'dic'),
    icDph: text(row, 'icDph'),
    iban: text(row, 'iban'),
    swift: text(row, 'swift'),
    bankName: text(row, 'bankName'),
    fromEmail: text(row, 'fromEmail'),
    invoiceNumberPrefix: text(row, 'invoiceNumberPrefix'),
    nextInvoiceNumber: text(row, 'nextInvoiceNumber'),
    defaultDueDays: num(row, 'defaultDueDays'),
    // Percentages arrive as decimal strings — `"23.00"`, `"1.00"`.
    defaultVatRatePct: num(row, 'defaultVatRatePct'),
    revenueSharePct: num(row, 'revenueSharePct'),
    autoBillingEnabled: row.autoBillingEnabled === true,
    autoEmailInvoices: row.autoEmailInvoices === true,
    paymentSquareEnabled: row.paymentSquareEnabled === true,
    address: text(row, 'address'),
    city: text(row, 'city'),
    zip: text(row, 'zip'),
    country: text(row, 'country'),
    registrationNote: text(row, 'registrationNote'),
    fromName: text(row, 'fromName'),
    footerText: text(row, 'footerText'),
    logoUrl: text(row, 'logoUrl'),
    accentColor: text(row, 'accentColor'),
    invoiceEmailSubject: text(row, 'invoiceEmailSubject'),
    invoiceEmailBody: text(row, 'invoiceEmailBody'),
    resendApiKeySet: row.resendApiKeySet === true,
  }
}

export type IbanState = 'missing' | 'invalid' | 'ok'

export function ibanState(settings: PlatformBillingSettings): IbanState {
  if (settings.iban === '') return 'missing'
  return isValidIban(settings.iban) ? 'ok' : 'invalid'
}

export function ibanNote(settings: PlatformBillingSettings): string {
  const state = ibanState(settings)
  if (state === 'missing') return tt('Bez IBAN-u faktúry neuvádzajú, kam zaplatiť.')
  if (state === 'invalid') return tt('IBAN neprešiel kontrolou - overte ho, kým sa naň nepošle platba.')
  return ''
}

/**
 * Whether the PayBySquare switch actually produces a QR.
 *
 * On **and** usable are different things, and the screen was showing only the first.
 */
export function squareIsEffective(settings: PlatformBillingSettings): boolean {
  return settings.paymentSquareEnabled && ibanState(settings) === 'ok'
}

export function squareNote(settings: PlatformBillingSettings): string {
  if (!settings.paymentSquareEnabled) return ''
  if (squareIsEffective(settings)) return ''
  return tt('PayBySquare je zapnutý, ale bez platného IBAN-u sa QR kód na faktúru nevytlačí.')
}

/**
 * Whether automatic e-mailing can actually deliver — the platform half of FB-S05.
 *
 * `BillingRunner` also needs each franchise's own `billing_email`, which this screen cannot see; that
 * check lives on Billing, beside the invoices it affects.
 */
export function autoEmailNote(settings: PlatformBillingSettings): string {
  if (!settings.autoEmailInvoices) return ''
  if (settings.fromEmail === '') return tt('Automatické odosielanie je zapnuté, ale nie je nastavený odosielateľ.')
  return ''
}

/**
 * `MM202607-0002` — what the next invoice will be called, so the prefix is not a guess.
 *
 * **It used to work this out here, and got it wrong three ways** - §3.29. The screen's label is
 * *Ďalšia faktúra*, a promise rather than an illustration, and this returned
 * `${prefix}${now.getFullYear()}${now.getMonth() + 1}-0001`, which disagreed with
 * `InvoiceNumbers.next` on:
 *
 * - **the sequence**, hardcoded `0001` where the server counts what the month already holds. The
 *   owner's June and July each hold two invoices, so `count + 1` really does produce `-0002`; their
 *   September holds none, which is the only reason `0001` ever looked right. §3.19 wired the button
 *   that breaks it.
 * - **the zone**, the browser's where the server uses **UTC**. Between 00:00 and 02:00 Bratislava
 *   time on the first of a month the two name different months.
 * - **what the number is for**, *now* rather than the invoice's own period.
 *
 * So it does not compute it any more: the server publishes `nextInvoiceNumber` from the same
 * generator the raise path calls, and a client that does not derive cannot derive it differently.
 *
 * **The fallback is the shape only**, for a payload that predates the field - and it keeps the old
 * arithmetic deliberately, because an older server is one whose sequence this cannot know either.
 */
export function numberingExample(settings: PlatformBillingSettings, now: Date): string {
  if (settings.nextInvoiceNumber !== '') return settings.nextInvoiceNumber
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  return `${settings.invoiceNumberPrefix}${year}${month}-0001`
}

/* §3.128 - **`revenueShareCents` is removed, and it was a money function nothing called.**
   `BillingRunner`'s own comment says why: *"§10A.181 - `revenue_share_pct` is no longer read here. The
   share is a pricing rule now."* This helper took **one platform-wide percentage** and applied it to a
   month, which is exactly the *one figure for every franchise* that §10A.181 replaced with a per-franchise
   rule - so a future screen reaching for the obvious-looking helper would have printed a figure **no
   invoice carries**. Its only three hits in the whole frontend were its declaration and its own two
   assertions. §10A.229 removed `unprintedCloseStatus` for the same reason the same week.

   `revenueSharePct` stays on the read model deliberately: the server still answers it, and the stored
   figure is the only record of what was charged before `V7` carried it into a rule. */

export function vatOf(settings: PlatformBillingSettings, netEuros: string | number | null): number {
  const net = parseMoneyToCents(netEuros)
  return Math.round((net * settings.defaultVatRatePct) / 100)
}

// ─── §3.14 - the supplier details, settable at last ───────────────────────────────────────────────

/**
 * The text fields `PUT …/platform-billing` accepts.
 *
 * **FB-505's shape, still live after the whole of Phase S.** `PlatformBillingPage` computed
 * `ibanState`, drew an amber border when it was not `ok`, printed `ibanNote` - and had no way to set
 * it. Measured on the owner's row: `iban` is **empty** while `auto_billing_enabled` and
 * `auto_email_invoices` are both true, so the run mails invoices with a dash where the account
 * number belongs and no pay-by-square QR (`PayBySquare.png` returns null on a blank IBAN).
 *
 * `resendApiKey` is here as a **write-only** member: it is never read back, so a draft built from
 * the response leaves it empty and a save sends it only when somebody has typed one.
 */
export const SUPPLIER_FIELDS = [
  'companyName',
  'address',
  'city',
  'zip',
  'country',
  'ico',
  'dic',
  'icDph',
  'iban',
  'swift',
  'bankName',
  'registrationNote',
  'fromEmail',
  'fromName',
  'invoiceNumberPrefix',
  'footerText',
  'logoUrl',
  'accentColor',
  'invoiceEmailSubject',
  'invoiceEmailBody',
] as const

/**
 * The three booleans `PUT …/platform-billing` accepts - `SAAS.md §3.114`.
 *
 * **FB-505's shape a third time, and on the same page.** The screen printed all three - *Automatická
 * fakturácia: zapnutá*, *Automatické odosielanie*, *PayBySquare: zapnutý* - and offered no way to set
 * any of them. Two of the three decide whether the platform raises invoices at all and whether it
 * mails them, which makes them the two most consequential switches in the product and the two nobody
 * could reach.
 *
 * **§3.108's ratchet passed them**, which is the part worth keeping: its flag check asked whether the
 * page *names* the flag, and a read-only `<p>` satisfies that. Its own `@DisplayName` says *a flag
 * with no switch is a setting nobody can set*, and that is exactly what it was looking at.
 * `agos-a-check-that-passes-on-nothing`, one level up - the check was real and its question was too
 * weak. It now asks whether the flag reaches a patch, and that `runPatch` loops this list.
 */
export const BILLING_FLAGS = [
  'autoBillingEnabled',
  'autoEmailInvoices',
  'paymentSquareEnabled',
] as const

export type BillingFlag = (typeof BILLING_FLAGS)[number]

/**
 * The two numbers on every invoice the platform raises - §3.114.
 *
 * `defaultDueDays` sets the due date and `defaultVatRatePct` the tax line. Both were displayed and
 * neither was editable, and neither is in §3.108's census either: that test reads `TEXT` and `FLAGS`,
 * while these two are written by `patch.integer` and `patch.decimal`.
 */
export const BILLING_NUMBERS = ['defaultDueDays', 'defaultVatRatePct'] as const

export type BillingNumber = (typeof BILLING_NUMBERS)[number]

/**
 * What the run form sends, and only what changed.
 *
 * **A flag goes only when it differs**, for `supplierPatch`'s reason: `update` writes the keys the body
 * names, so resending an unchanged `autoBillingEnabled` would rewrite the one switch somebody may have
 * just turned off in another tab.
 *
 * **A number is refused rather than coerced.** An empty box or a non-number is *leave it alone*, never
 * zero: `defaultDueDays: 0` would date every invoice due on the day it is raised, and
 * `defaultVatRatePct: 0` would issue them with no tax. Neither is a plausible thing to have meant by
 * clearing a box.
 */
export function runPatch(
  flags: Record<BillingFlag, boolean>,
  numbers: Record<BillingNumber, string>,
  stored: PlatformBillingSettings,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  for (const flag of BILLING_FLAGS) {
    if (flags[flag] !== stored[flag]) patch[flag] = flags[flag]
  }
  for (const key of BILLING_NUMBERS) {
    const typed = numbers[key].trim()
    if (typed === '') continue
    const parsed = Number(typed.replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed < 0) continue
    if (parsed === stored[key]) continue
    patch[key] = parsed
  }
  return patch
}

/** The flags as the form holds them, seeded from the response. */
export function flagsOf(settings: PlatformBillingSettings): Record<BillingFlag, boolean> {
  return {
    autoBillingEnabled: settings.autoBillingEnabled,
    autoEmailInvoices: settings.autoEmailInvoices,
    paymentSquareEnabled: settings.paymentSquareEnabled,
  }
}

/** The numbers as the form holds them - strings, so a half-typed value is not reformatted. */
export function numbersOf(settings: PlatformBillingSettings): Record<BillingNumber, string> {
  return {
    defaultDueDays: String(settings.defaultDueDays),
    defaultVatRatePct: String(settings.defaultVatRatePct),
  }
}

/** What each control is called. Legacy's own labels. */
export function runFieldLabel(key: BillingFlag | BillingNumber): string {
  if (key === 'autoBillingEnabled') return tt('Automaticky generovať faktúry')
  if (key === 'autoEmailInvoices') return tt('Automaticky posielať PDF emailom')
  if (key === 'paymentSquareEnabled') return tt('PayBySquare QR na faktúre')
  if (key === 'defaultDueDays') return tt('Predvolená splatnosť (dní)')
  return tt('Predvolená DPH %')
}

export type SupplierField = (typeof SUPPLIER_FIELDS)[number]

export type SupplierDraft = Record<SupplierField, string> & { resendApiKey: string }

export function supplierDraftOf(settings: PlatformBillingSettings): SupplierDraft {
  const draft = { resendApiKey: '' } as SupplierDraft
  for (const field of SUPPLIER_FIELDS) {
    draft[field] = settings[field]
  }
  return draft
}

/**
 * Only what changed - and `resendApiKey` only when typed.
 *
 * `PlatformBillingSettingsService.update` writes the keys the body names, so resending an unchanged
 * `resend_api_key` would rewrite a credential for nothing. **And the key is the one field with no
 * original to compare against**, because the server answers a boolean rather than the value: an
 * empty box means *leave it alone*, never *clear it*.
 */
export function supplierPatch(
  draft: SupplierDraft,
  original: SupplierDraft,
): Record<string, string> {
  const patch: Record<string, string> = {}
  for (const field of SUPPLIER_FIELDS) {
    if (draft[field].trim() !== original[field].trim()) {
      patch[field] = draft[field].trim()
    }
  }
  if (draft.resendApiKey.trim() !== '') {
    patch.resendApiKey = draft.resendApiKey.trim()
  }
  return patch
}

export function maySaveSupplier(draft: SupplierDraft, original: SupplierDraft): boolean {
  return Object.keys(supplierPatch(draft, original)).length > 0
}

/**
 * Whether this field is the one the banner is complaining about.
 *
 * **The banner and the box that fixes it read the same function**, so they cannot disagree about
 * whether the IBAN is acceptable - which is the failure a second copy of the rule would introduce.
 */
export function fieldIsFaulty(field: SupplierField, draft: SupplierDraft): boolean {
  if (field !== 'iban') return false
  return ibanState({ ...({} as PlatformBillingSettings), iban: draft.iban }) !== 'ok'
}

/**
 * What a blank IBAN costs, said on the screen.
 *
 * The page warned without saying what follows. These are the two measured consequences, and the
 * second is why the first is not merely cosmetic: the run is enabled and it mails what it raises.
 */
export function blankIbanConsequence(settings: PlatformBillingSettings): string {
  if (ibanState(settings) === 'ok') return ''
  const mailed = settings.autoBillingEnabled && settings.autoEmailInvoices
  const base = tt(
    'Faktúry sa vystavia bez QR kódu na platbu a namiesto čísla účtu bude pomlčka.',
  )
  return mailed
    ? `${base} ${tt('Automatická fakturácia je zapnutá a faktúry sa odosielajú - tieto faktúry sa nedajú zaplatiť.')}`
    : base
}
